import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/button/Button';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Table from '../components/table/table';
import Icon from '../components/icon/Icon';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import DropDown, { ItemProp } from '../components/dropdown/DropDown';
import CasfogSubmissionDetailModal from '../components/modals/CasfogSubmissionDetailModal';
import { ITableData } from '../types/interfaces';

import { 
  getCasfogSubmissions, 
  SubmissionSummary,
  getAnnexes,
  AnnexItem
} from '../services/AnnexesService';
import { routes } from '../routes/routePaths';

type SubmissionStatus = 'PENDING' | 'SUCCESS' | 'ERROR' | 'TIMEOUT';

const statusConfig: Record<SubmissionStatus, { label: string; color: string }> = {
  PENDING: { label: 'Pendiente', color: '#f5a623' },
  SUCCESS: { label: 'Exitoso', color: '#4caf50' },
  ERROR: { label: 'Error', color: '#f44336' },
  TIMEOUT: { label: 'Timeout', color: '#ff9800' },
};

const CasfogSubmissionHistory = () => {
  const navigate = useNavigate();
  const name = localStorage.getItem('name');
  
  const [submissions, setSubmissions] = useState<SubmissionSummary[]>([]);
  const [annexes, setAnnexes] = useState<AnnexItem[]>([]);
  const [tableData, setTableData] = useState<ITableData>();
  const [page, setPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const [selectedAnnexIds, setSelectedAnnexIds] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<number | null>(null);

  const currentYear = new Date().getFullYear();
  const yearItems: ItemProp[] = [
    { label: 'Todos los años' },
    ...Array.from({ length: 10 }, (_, i) => ({
      label: (currentYear - i).toString(),
    }))
  ];

  const monthItems: ItemProp[] = [
    { label: 'Todos los meses' },
    { label: 'Enero (1)' },
    { label: 'Febrero (2)' },
    { label: 'Marzo (3)' },
    { label: 'Abril (4)' },
    { label: 'Mayo (5)' },
    { label: 'Junio (6)' },
    { label: 'Julio (7)' },
    { label: 'Agosto (8)' },
    { label: 'Septiembre (9)' },
    { label: 'Octubre (10)' },
    { label: 'Noviembre (11)' },
    { label: 'Diciembre (12)' },
  ];

  const statusItems: ItemProp[] = [
    { label: 'Todos los estados' },
    { label: 'Pendiente' },
    { label: 'Exitoso' },
    { label: 'Error' },
    { label: 'Timeout' },
  ];

  useEffect(() => {
    getAnnexes(0, 100, '')
      .then((response: any) => {
        if (response?.success && response?.data?.content) {
          const casfogAnnexes = response.data.content.filter(
            (a: AnnexItem) => a.casfog_enabled
          );
          setAnnexes(casfogAnnexes);
        }
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [page, selectedAnnexIds, selectedYear, selectedMonth, selectedStatus]);

  const fetchSubmissions = async () => {
    setIsLoading(true);
    try {
      const statusMap: Record<string, string> = {
        'Pendiente': 'PENDING',
        'Exitoso': 'SUCCESS',
        'Error': 'ERROR',
        'Timeout': 'TIMEOUT',
      };

      const data = await getCasfogSubmissions(
        selectedAnnexIds.length > 0 ? selectedAnnexIds : undefined,
        selectedYear || undefined,
        selectedMonth || undefined,
        selectedStatus ? statusMap[selectedStatus] : undefined,
        undefined,
        page,
        20
      );

      setSubmissions(data.content);
      setTableData({
        size: data.size,
        page: data.page,
        totalElements: data.totalElements,
        totalPages: data.totalPages,
      });
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setSubmissions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (newPage: number) => {
    if (newPage < 0 || newPage === tableData?.totalPages) return;
    setPage(newPage);
  };

  const handleYearChange = (yearLabel: string) => {
    if (yearLabel === 'Todos los años') {
      setSelectedYear(null);
    } else {
      setSelectedYear(parseInt(yearLabel));
    }
    setPage(0);
  };

  const handleMonthChange = (monthLabel: string) => {
    if (monthLabel === 'Todos los meses') {
      setSelectedMonth(null);
    } else {
      const monthMatch = monthLabel.match(/\((\d+)\)/);
      if (monthMatch) {
        setSelectedMonth(parseInt(monthMatch[1]));
      }
    }
    setPage(0);
  };

  const handleStatusChange = (statusLabel: string) => {
    if (statusLabel === 'Todos los estados') {
      setSelectedStatus(null);
    } else {
      setSelectedStatus(statusLabel);
    }
    setPage(0);
  };

  const handleViewDetail = (submission: SubmissionSummary) => {
    setSelectedSubmissionId(submission.id);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedSubmissionId(null);
  };

  const formatPeriod = (year: number, month: number) => {
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${monthNames[month - 1]} ${year}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const StatusBadge = ({ status }: { status: string }) => {
    const config = statusConfig[status as SubmissionStatus];
    return (
      <Card
        spacing="1"
        display="inline-flex"
        style={{
          backgroundColor: config?.color || '#999',
          color: 'white',
          borderRadius: '4px',
          padding: '2px 8px',
          fontSize: '12px',
        }}
      >
        {config?.label || status}
      </Card>
    );
  };

  const columns = [
    {
      name: 'Anexo',
      selector: (item: SubmissionSummary) => (
        <P type='2' variant='neutral' withMargin={false}>
          {item.annexCode || item.annexName || `ID ${item.annexId}`}
        </P>
      ),
    },
    {
      name: 'Período',
      selector: (item: SubmissionSummary) => (
        <P type='2' variant='neutral' withMargin={false}>
          {formatPeriod(item.year, item.month)}
        </P>
      ),
    },
    {
      name: 'Tipo',
      selector: (item: SubmissionSummary) => (
        <P type='2' variant='neutral' withMargin={false}>
          {item.submissionType === 'ORIGINAL' ? 'Original' : 'Rectificativa'}
        </P>
      ),
    },
    {
      name: 'Estado',
      selector: (item: SubmissionSummary) => (
        <StatusBadge status={item.status} />
      ),
      center: true,
    },
    {
      name: 'Filas',
      selector: (item: SubmissionSummary) => (
        <P type='2' variant='neutral' withMargin={false}>
          {item.rowsProcessed ?? '-'}
        </P>
      ),
      center: true,
    },
    {
      name: 'Errores',
      selector: (item: SubmissionSummary) => (
        <P type='2' variant={item.errorCount > 0 ? 'error' : 'neutral'} withMargin={false}>
          {item.errorCount}
        </P>
      ),
      center: true,
    },
    {
      name: 'Fecha envío',
      selector: (item: SubmissionSummary) => (
        <P type='2' variant='neutral' withMargin={false}>
          {formatDate(item.submittedAt)}
        </P>
      ),
    },
    {
      name: 'Acciones',
      selector: (item: SubmissionSummary) => (
        <Card display='flex' gap='8px' justifyContent='center'>
          <Icon
            name='view_outline'
            width={24}
            height={24}
            cursor='pointer'
            variant='info'
            onClick={() => handleViewDetail(item)}
            title='Ver detalle'
          />
        </Card>
      ),
      center: true,
    },
  ];

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing='4'
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between'>
        <Card display='flex' alignItems='center'>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Historial de Envíos CASFOG
          </H>
        </Card>
        <Button variant='secondary' onClick={() => navigate(routes.annexes)}>
          Volver a Anexos
        </Button>
      </Card>

      <Card display='flex' gap='16px' margin={{ top: '4' }} flexWrap='wrap'>
        <Card width='200px'>
          <DropDown
            id="year-filter"
            label="Año"
            title={selectedYear?.toString() || 'Todos los años'}
            items={yearItems}
            onChange={handleYearChange}
            size="md"
          />
        </Card>
        <Card width='200px'>
          <DropDown
            id="month-filter"
            label="Mes"
            title={
              selectedMonth 
                ? monthItems.find(item => item.label.includes(`(${selectedMonth})`))?.label || 'Todos los meses'
                : 'Todos los meses'
            }
            items={monthItems}
            onChange={handleMonthChange}
            size="md"
          />
        </Card>
        <Card width='200px'>
          <DropDown
            id="status-filter"
            label="Estado"
            title={selectedStatus || 'Todos los estados'}
            items={statusItems}
            onChange={handleStatusChange}
            size="md"
          />
        </Card>
      </Card>

      {isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : submissions.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={submissions}
            tableData={tableData}
            handleChangePage={handleChangePage}
          />
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay envíos registrados.
          </P>
          <P variant='primary' align='center'>
            Los envíos a CASFOG aparecerán aquí.
          </P>
        </Card>
      )}

      {selectedSubmissionId && (
        <CasfogSubmissionDetailModal
          isOpen={showDetailModal}
          onClose={handleCloseDetailModal}
          submissionId={selectedSubmissionId}
        />
      )}
    </Card>
  );
};

export default CasfogSubmissionHistory;
